import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import styled from "styled-components";
import { ShieldCheck, ShieldOff, Building2 } from "lucide-react";

import { Button } from "./common/ui/button";
import { useToast } from "./common/ui/use-toast";
import { getUserConsents, revokeUserConsent } from "../services/index";

interface ConsentRecord {
  id: string;
  business_id: string;
  business_name: string;
  scope: string;
  created_at: string;
}

const formatDate = (value: string) => {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const WalletAccess = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["userConsents"],
    queryFn: getUserConsents,
  });

  const revokeMutation = useMutation({
    mutationKey: ["revokeConsent"],
    mutationFn: (consentId: string) => revokeUserConsent(consentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userConsents"] });
      setRevokingId(null);
      toast({ description: "Wallet access revoked successfully." });
    },
    onError: () => {
      setRevokingId(null);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to revoke access. Please try again.",
      });
    },
  });

  const handleRevoke = (consentId: string) => {
    setRevokingId(consentId);
    revokeMutation.mutate(consentId);
  };

  const consents: ConsentRecord[] = data?.data ?? [];

  return (
    <Shell>
      <Header>
        <div>
          <Eyebrow>PERMISSIONS</Eyebrow>
          <Title>Wallet access</Title>
          <Description>
            Businesses listed below have been granted permission to view your wallet balance.
            You can revoke access at any time.
          </Description>
        </div>
        <HeaderBadge $tone={consents.length > 0 ? "amber" : "emerald"}>
          <ShieldCheck size={14} />
          {consents.length > 0
            ? `${consents.length} active ${consents.length === 1 ? "grant" : "grants"}`
            : "No active grants"}
        </HeaderBadge>
      </Header>

      {isLoading && (
        <EmptyCard>
          <EmptyText>Loading your consent records...</EmptyText>
        </EmptyCard>
      )}

      {isError && (
        <EmptyCard>
          <EmptyText>Failed to load consent records. Please refresh the page.</EmptyText>
        </EmptyCard>
      )}

      {!isLoading && !isError && consents.length === 0 && (
        <EmptyCard>
          <EmptyIcon>
            <ShieldCheck size={32} />
          </EmptyIcon>
          <EmptyTitle>No businesses have access to your wallet</EmptyTitle>
          <EmptyText>
            When a business requests access to view your wallet balance and you approve it,
            it will appear here.
          </EmptyText>
        </EmptyCard>
      )}

      {!isLoading && !isError && consents.length > 0 && (
        <ConsentList>
          {consents.map((consent) => (
            <ConsentRow key={consent.id}>
              <ConsentIcon>
                <Building2 size={18} />
              </ConsentIcon>
              <ConsentInfo>
                <ConsentName>{consent.business_name}</ConsentName>
                <ConsentMeta>
                  {consent.scope === "balance" ? "Wallet balance access" : consent.scope}
                  {" · "}
                  Granted {formatDate(consent.created_at)}
                </ConsentMeta>
              </ConsentInfo>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={revokingId === consent.id}
                onClick={() => handleRevoke(consent.id)}
              >
                <ShieldOff size={14} />
                {revokingId === consent.id ? "Revoking..." : "Revoke"}
              </Button>
            </ConsentRow>
          ))}
        </ConsentList>
      )}
    </Shell>
  );
};

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Eyebrow = styled.p`
  margin: 0 0 0.35rem;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: ${(props) => props.theme.colors.textSecondary};
`;

const Title = styled.h2`
  margin: 0 0 0.4rem;
  font-size: 1.35rem;
  font-weight: 800;
  color: ${(props) => props.theme.colors.text};
`;

const Description = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.6;
  color: ${(props) => props.theme.colors.textSecondary};
  max-width: 38rem;
`;

const HeaderBadge = styled.span<{ $tone: "emerald" | "amber" }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
  background: ${(props) =>
    props.$tone === "emerald" ? "rgba(16, 185, 129, 0.12)" : "rgba(245, 158, 11, 0.12)"};
  color: ${(props) => (props.$tone === "emerald" ? "#047857" : "#b45309")};
`;

const EmptyCard = styled.div`
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(250, 251, 253, 0.98));
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 20px;
  box-shadow: 0 20px 48px rgba(15, 23, 42, 0.04);
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;
`;

const EmptyIcon = styled.div`
  color: ${(props) => props.theme.colors.textSecondary};
  opacity: 0.5;
  margin-bottom: 0.5rem;
`;

const EmptyTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 0.88rem;
  color: ${(props) => props.theme.colors.textSecondary};
  line-height: 1.6;
  max-width: 26rem;
`;

const ConsentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ConsentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(250, 251, 253, 0.98));
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);
  padding: 1rem 1.25rem;

  @media (max-width: 640px) {
    flex-wrap: wrap;
  }
`;

const ConsentIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgba(148, 163, 184, 0.1);
  color: ${(props) => props.theme.colors.textSecondary};
  flex-shrink: 0;
`;

const ConsentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ConsentName = styled.p`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
`;

const ConsentMeta = styled.p`
  margin: 0.2rem 0 0;
  font-size: 0.82rem;
  color: ${(props) => props.theme.colors.textSecondary};
`;

export default WalletAccess;
